import { AppTextFieldHandler } from "../../../app/appTypes"
import { useAppDispatch, useAppSelector } from "../../../app/hooks"
import { Alert, DialogActions, DialogContent, DialogTitle, FormControl, Grid, InputLabel, MenuItem, Select, Tab, TextField, Box, Typography } from "@mui/material"
import { Localized, useLocalization } from "@fluent/react"
import { If } from "../../../app/If"
import { TabContext, TabList, TabPanel } from "@mui/lab"
import { showSuccess } from "../../notifications/notificationsSlice"
import { useCallback, useState } from "react"
import { MailTemplate, updateSuperMailTemplate } from "./superMailTemplatesApi"
import { selectAllCompanies } from "../../company/companiesSlice"
import { SaveButton } from "../../../app/components/SaveButton"
import { CancelButton } from "../../../app/components/CancelButton"


export interface MailTemplateDialogProps {
    template: MailTemplate
    onSaveSuccess: (updatedTemplate: MailTemplate) => void
    onCancel: () => void
}

export const MailTemplateDialog = ({ template,  onSaveSuccess, onCancel }: MailTemplateDialogProps) => {
    const dispatch = useAppDispatch()
    const { l10n } = useLocalization()
    
    const companies = useAppSelector(selectAllCompanies);
    const type = template?.type ?? "";
    const [context, setContext] = useState<string>(template?.context ?? "DOCUMENT");
    const [body, setBody] = useState<string>(template?.body ?? "");
    const [bodyEN, setBodyEN] = useState<string>(template?.bodyEN ?? "");
    const [subject, setSubject] = useState<string>(template?.subject ?? "");
    const [subjectEN, setSubjectEN] = useState<string>(template?.subjectEN ?? "");
    const [saving, setSaving] = useState(false)
    const [serverError, setServerError] = useState<string | null>(null)
    const [tab, setTab] = useState<"polish" | "english">("polish");

    const isFixed = template?.isFixed ?? false
    const companyName = template?.companyId
        ? companies.find(company => String(company.id) === String(template.companyId))?.name
        : "System"
    const description = l10n.getString(`mail-type-${type}-description`, null, "")

    const handleBodyChange: AppTextFieldHandler = useCallback(e => setBody(e.target.value), [setBody]);
    const handleBodyENChange: AppTextFieldHandler = useCallback(e => setBodyEN(e.target.value), [setBodyEN]);
    const handleSubjectChange: AppTextFieldHandler = useCallback(e => setSubject(e.target.value), [setSubject]);
    const handleSubjectENChange: AppTextFieldHandler = useCallback(e => setSubjectEN(e.target.value), [setSubjectEN]);
    const handleContextChange = (e) => setContext(e.target.value);

    const handleTabChange = useCallback((_, newTab) => {
        setTab(newTab)
    }, [])

    const handleSaveClick = async () => {
        setSaving(true)
        try {
            let updatedTemplate = {
                id: template.id, context, body, subject, bodyEN, subjectEN
            };

            const result = await dispatch(updateSuperMailTemplate(updatedTemplate))
            if (updateSuperMailTemplate.fulfilled.match(result)) {
                dispatch(showSuccess("saved"))
                onSaveSuccess(result.payload)
            } else {
                let errorMsg = "error"
                if (result.payload && result.payload.kind === 'http') {
                    const problem = result.payload.problem
                    if (problem) {
                        errorMsg = problem.title

                    }
                }
                setServerError(errorMsg)
            }
        }
        catch (error) {
            setServerError("error");
        }
        finally {
            setSaving(false)
        }
    }

   return <>
        <DialogTitle>
            <Localized id="edit-mail-template">
                <span>Edycja szablonu</span>
            </Localized>
        </DialogTitle>
        <DialogContent sx={{ pb: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <Grid container spacing={2} padding={2} sx={{ height: '100%', pb: 0, minHeight: 0 }}>
                
                {/* LEWA STRONA */}
                <Grid item xs={6} sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    minHeight: 0,
                    pt: '0 !important'
                }}>
                    <Box sx={{ flexShrink: 0, mb: 1, mt: 2 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <If condition={isFixed}>
                                    <Alert severity="warning" sx={{ mb: 2 }}>
                                        <Localized id="mail-template-fixed-warning">
                                            <span>_Ten szablon jest wyłączony z edycji</span>
                                        </Localized>
                                    </Alert>
                                </If>
                            </Grid>
                            <Grid item xs={12} sm={6} sx={{ minWidth: 0 }}>
                                <Box sx={{
                                    minHeight: 40,
                                    px: 1.5,
                                    py: 0.75,
                                    border: 1,
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    backgroundColor: 'action.hover',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    minWidth: 0
                                }}>
                                    <Typography variant="caption" color="text.secondary" noWrap sx={{ flexShrink: 0 }}>
                                        <Localized id="mail-template-type">Typ</Localized>:
                                    </Typography>
                                    <Typography variant="body2" fontWeight={500} sx={{ minWidth: 0, overflowWrap: 'anywhere' }} title={type}>
                                        {type}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={6} sx={{ minWidth: 0 }}>
                                <Box sx={{
                                    minHeight: 40,
                                    px: 1.5,
                                    py: 0.75,
                                    border: 1,
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    backgroundColor: 'action.hover',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    minWidth: 0
                                }}>
                                    <Typography variant="caption" color="text.secondary" noWrap sx={{ flexShrink: 0 }}>
                                        <Localized id="mail-template-name">_Nazwa</Localized>:
                                    </Typography>
                                    <Typography variant="body2" fontWeight={500} sx={{ minWidth: 0, overflowWrap: 'anywhere' }} title={l10n.getString(`mail-type-${type}`, null, `_${type}`)}>
                                        {l10n.getString(`mail-type-${type}`, null, `_${type}`)}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={8} sx={{ order: { xs: 1, sm: 1 }, minWidth: 0 }}>
                                <Box sx={{
                                    minHeight: 40,
                                    px: 1.5,
                                    py: 0.75,
                                    border: 1,
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    backgroundColor: 'action.hover',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1
                                }}>
                                    <Typography variant="caption" color="text.secondary" noWrap sx={{ flexShrink: 0 }}>
                                        <Localized id="description">Opis</Localized>:
                                    </Typography>
                                    <Typography variant="body2" fontWeight={500} sx={{ minWidth: 0, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                                        {description || '-'}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={4} sx={{ minWidth: 0, order: { xs: 2, sm: 2 } }}>
                                <Box sx={{
                                    minHeight: 40,
                                    px: 1.5,
                                    py: 0.75,
                                    border: 1,
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    backgroundColor: 'action.hover',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    minWidth: 0
                                }}>
                                    <Typography variant="caption" color="text.secondary" noWrap sx={{ flexShrink: 0 }}>
                                        <Localized id="company">Firma</Localized>:
                                    </Typography>
                                    <Typography variant="body2" fontWeight={500} sx={{ minWidth: 0, overflowWrap: 'anywhere' }} title={companyName}>
                                        {companyName ?? "-"}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={6} sx={{ order: 3 }}>
                                <FormControl sx={{ width: '100%' }}>
                                    <InputLabel id="select-mail-context-label"><Localized id="mail-context">Kontekst</Localized></InputLabel>
                                    <Select
                                        labelId="select-mail-context-label"
                                        value={context}
                                        onChange={handleContextChange}
                                        label={<Localized id="mail-context">Kontekst</Localized>}
                                        sx={{ width: '100%' }}
                                        disabled={isFixed}
                                    >
                                        <MenuItem value={'DOCUMENT'}>
                                            <Localized id="mail-document">Dokument</Localized>
                                        </MenuItem>
                                        <MenuItem value={'SYSTEM'}>System</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Box>
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                        <TabContext value={tab}>
                            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                                <TabList onChange={handleTabChange}>
                                    <Tab
                                        key={0}
                                        value={"polish"}
                                        label={<Localized id="polish">_Polski</Localized>} />
                                    <Tab
                                        key={1}
                                        value={"english"}
                                        label={<Localized id="english">_Angielski</Localized>} />
                                </TabList>
                            </Box>
                            
                            <TabPanel value="polish" sx={{
                                flex: 1,
                                minHeight: 0,
                                display: tab === 'polish' ? 'flex' : 'none',
                                flexDirection: 'column',
                                p: 0,
                                pt: 2
                            }}>
                                <TextField
                                    fullWidth
                                    disabled={isFixed}
                                    value={subject}
                                    inputProps={{ maxLength: 100 }}
                                    onChange={handleSubjectChange}
                                    label={<Localized id="mail-subject">Temat PL</Localized>}
                                />
                                <TextField
                                    sx={{
                                        marginTop: 2,
                                        flex: 1,
                                        minHeight: 0,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        '& .MuiInputBase-root': {
                                            flex: 1,
                                            alignItems: 'flex-start',
                                            overflow: 'hidden'
                                        },
                                        '& textarea': {
                                            height: '100% !important',
                                            overflowY: 'auto !important'
                                        }
                                    }}
                                    fullWidth
                                    disabled={isFixed}
                                    value={body}
                                    multiline
                                    inputProps={{ maxLength: 10000 }}
                                    onChange={handleBodyChange}
                                    label={<Localized id="mail-body">Treść PL</Localized>}
                                />
                            </TabPanel>

                            <TabPanel value="english" sx={{
                                flex: 1,
                                minHeight: 0,
                                display: tab === 'english' ? 'flex' : 'none',
                                flexDirection: 'column',
                                p: 0,
                                pt: 2
                            }}>
                                <TextField
                                    fullWidth
                                    disabled={isFixed}
                                    value={subjectEN}
                                    inputProps={{ maxLength: 100 }}
                                    onChange={handleSubjectENChange}
                                    label={<Localized id="mail-subject-en">Temat EN</Localized>}
                                />
                                <TextField
                                    sx={{
                                        marginTop: 2,
                                        flex: 1,
                                        minHeight: 0,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        '& .MuiInputBase-root': {
                                            flex: 1,
                                            alignItems: 'flex-start',
                                            overflow: 'hidden'
                                        },
                                        '& textarea': {
                                            height: '100% !important',
                                            overflowY: 'auto !important'
                                        }
                                    }}
                                    fullWidth
                                    disabled={isFixed}
                                    value={bodyEN}
                                    multiline
                                    inputProps={{ maxLength: 10000 }}
                                    onChange={handleBodyENChange}
                                    label={<Localized id="mail-body-en">Treść EN</Localized>}
                                />
                            </TabPanel>
                        </TabContext>
                    </Box>
                </Grid>
                
                {/* PRAWA STRONA */}
                <Grid item xs={6} sx={{ overflow: 'auto', height: '100%', mt: 2 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}><Localized id="mail-preview-full">_Podgląd całego maila</Localized></Typography>
                    <Box sx={{ mb: 2 }}>
                        <strong><Localized id="mail-subject-label">_Temat:</Localized></strong> {tab === "polish" ? subject : subjectEN}
                    </Box>
                    <Box sx={{ p: 2, border: '1px solid #ccc', backgroundColor: '#f9f9f9', mb: 2 }}>
                        <div dangerouslySetInnerHTML={{ __html: (tab === "polish" ? body : bodyEN)/* + (selectedFooter ? selectedFooter.footer : '') */ }} />
                    </Box>
                </Grid>
            </Grid>
            {
                serverError && <Alert sx={{ marginTop: 1, }} onClose={() => { setServerError("") }} severity="error">
                    <Localized id={serverError}>
                        <span>Server error</span>
                    </Localized>
                </Alert>
            }
        </DialogContent>
        <DialogActions>
            <If condition={!isFixed}>
                <SaveButton onClick={handleSaveClick} loading={saving}/>
            </If>
            <CancelButton loading={saving} onClick={onCancel}/> 
        </DialogActions>
    </>
}